import { mapContactSelectedColumnsToRegularColumns } from "app/pages/sales-intelligence/modules/enrichment/utils/utils";
import { fetchContactsUserQuotaThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import { parseLinkedInAndNames } from "app/pages/sales-intelligence/sub-modules/management/store/effects/parsing";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";
import type {
    EnrichmentConfigItem,
    PostContactsCsvPropsFormData,
} from "app/pages/sales-intelligence/modules/enrichment/store/type";
import * as actions from "app/pages/sales-intelligence/modules/enrichment/store/actions/enrichment";
import type {
    ContactsParsingErrors,
    ParsedContactsResult,
} from "app/pages/sales-intelligence/sub-modules/management/components/types";

const logUploadFailedContactsEnrichmentCsvThunk =
    (file: File) =>
    async (
        _dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<void> => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            await api.enrichment.postContactsEnrichmentCsvUploadFailed(formData);
        } catch (error) {
            return error;
        }
    };

export const parseContactsEnrichmentCsvThunk =
    (csvFile: File, quotaRemaining: number) =>
    async (dispatch: ThunkDispatchCommon): Promise<ParsedContactsResult | ContactsParsingErrors> => {
        try {
            dispatch(actions.parseContactsEnrichmentCsvAsync.request());

            const parseResult = await parseLinkedInAndNames(csvFile, quotaRemaining);

            dispatch(actions.parseContactsEnrichmentCsvAsync.success(parseResult));
            return parseResult;
        } catch (error) {
            dispatch(actions.parseContactsEnrichmentCsvAsync.failure(error));
            void dispatch(logUploadFailedContactsEnrichmentCsvThunk(csvFile));

            return error;
        }
    };

export const getContactsSelectedColumnsConfigThunk =
    () =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<EnrichmentConfigItem[]> => {
        try {
            dispatch(actions.getContactsSelectedColumnsConfigAsync.request());

            const result = await api.enrichment.getContactsSelectedColumnsConfig();
            const mappedResult = mapContactSelectedColumnsToRegularColumns(result);
            dispatch(actions.getContactsSelectedColumnsConfigAsync.success(mappedResult));

            return mappedResult;
        } catch (error) {
            dispatch(actions.getContactsSelectedColumnsConfigAsync.failure(error));
            return error;
        }
    };

export const postContactsEnrichmentCsvThunk =
    (props: PostContactsCsvPropsFormData) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<string | null> => {
        try {
            dispatch(actions.postContactsEnrichmentCsvAsync.request());

            const response = await api.enrichment.postContactsCsv(props);

            dispatch(actions.postContactsEnrichmentCsvAsync.success());

            return response;
        } catch (error) {
            dispatch(actions.postContactsEnrichmentCsvAsync.failure(error));
            return null;
        }
    };

export const getEnrichedContactsCsvThunk =
    (id: string) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { api } }: ThunkDependencies,
    ): Promise<string> => {
        try {
            dispatch(actions.getEnrichedCsvAsync.request());

            const result = await api.enrichment.getEnrichedContactsCsv(id);

            dispatch(actions.getEnrichedCsvAsync.success(result));
            void dispatch(fetchContactsUserQuotaThunk());

            return result;
        } catch (error) {
            dispatch(actions.getEnrichedCsvAsync.failure(error));
            return error;
        }
    };
